import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, Avatar, Menu, MenuItem } from '@mui/material';
import { useThemeManager } from '../context/ThemeContext';
import envConfig from '../config';

declare global {
  interface Window {
    google: any;
  }
}

interface GoogleSignInButtonProps {
  googleIdToken?: string | null;
  onSignInSuccess: (token: string) => void;
  onSignOutSuccess?: () => void;
}

interface DecodedToken {
  name: string;
  picture: string;
  email: string;
  exp: number;
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({ googleIdToken, onSignInSuccess, onSignOutSuccess }) => {
  const { mode } = useThemeManager();
  const clientId = envConfig.googleClientId;
  const [user, setUser] = useState<DecodedToken | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isTokenExpired = async (token: string) => {
    try {
      const jwtDecode = (await import('jwt-decode')).default;
      const decoded: DecodedToken = jwtDecode(token);
      if (decoded.exp * 1000 < Date.now()) {
        return true;
      }
      return false;
    } catch (error) {
      return true;
    }
  };

  useEffect(() => {
    const checkToken = async () => {
      if (googleIdToken) {
        if (await isTokenExpired(googleIdToken)) {
          console.log("Token expired.");
          setUser(null);
          localStorage.removeItem('googleIdToken');
          if (onSignOutSuccess) {
            onSignOutSuccess();
          }
        } else {
          console.log("Google ID Token received:", googleIdToken);
          try {
            const jwtDecode = (await import('jwt-decode')).default;
            const decoded: DecodedToken = jwtDecode(googleIdToken);
            console.log("User picture URL:", decoded.picture);
            setUser(decoded);
          } catch (error) {
            console.error("Failed to decode JWT token or invalid token:", error);
            setUser(null);
            localStorage.removeItem('googleIdToken'); // Remove invalid token
            if (onSignOutSuccess) {
              onSignOutSuccess(); // Clear invalid token
            }
          }
        }
      } else {
        console.log("No Google ID Token.");
        setUser(null);
      }
    };
    checkToken();
  }, [googleIdToken, onSignOutSuccess]);

  const handleCredentialResponse = useCallback((response: any) => {
    console.log("Encoded JWT ID token: " + response.credential);
    localStorage.setItem('googleIdToken', response.credential);
    onSignInSuccess(response.credential);
  }, [onSignInSuccess]);

  useEffect(() => {
    if (window.google && !googleIdToken) {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
        scope: 'email profile',
      });

      window.google.accounts.id.renderButton(
        document.getElementById("signInDiv"),
        { theme: mode === 'dark' ? 'filled_black' : 'outline', size: "large" }  // customization attributes
      );
    }
  }, [googleIdToken, clientId, handleCredentialResponse, mode]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    localStorage.removeItem('googleIdToken');
    setUser(null);
    if (onSignOutSuccess) {
      onSignOutSuccess();
    }
    if (window.google) {
      window.google.accounts.id.disableAutoSelect(); // Prevent automatic re-login
    }
    handleClose();
  };

  return (
    <Box sx={{ marginLeft: '16px !important' }}>
      <Box sx={{ display: googleIdToken && user ? 'block' : 'none' }}>
        {user && (
          <>
            <Button
              onClick={handleMenu}
              color="inherit"
              startIcon={
                <Avatar src={user.picture ? `${user.picture}?sz=100` : undefined} alt={user.name} sx={{ width: 24, height: 24 }}>
                  {!user.picture && user.name && user.name.charAt(0)}
                </Avatar>
              }
            >
              {user.name}
            </Button>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={open}
              onClose={handleClose}
            >
              <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
            </Menu>
          </>
        )}
      </Box>

      <Box sx={{ display: googleIdToken && user ? 'none' : 'block' }}>
        <div id="signInDiv"></div>
      </Box>
    </Box>
  );
};

export default GoogleSignInButton;